import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import TechniqueCards from "@/components/TechniqueCards";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Check } from "@/components/Icon";
import samplesClothing from "@/assets/samples-clothing.webp";
import teamShirtsAsset from "@/assets/bedrijven-rotterdam.webp.asset.json";

const HERO_IMAGE = "/hero-magicprint.webp";
const H1 = "T-shirts bedrukken in Rotterdam";

const linkCls = "font-medium text-primary underline underline-offset-4 hover:no-underline";
const h2Cls = "text-3xl font-bold text-foreground md:text-4xl";
const bodyCls = "mt-5 space-y-4 text-lg leading-relaxed text-muted-foreground";

const faqs = [
  { q: "Wat is de minimale oplage?", a: "Bij Magic Print kun je T-shirts laten bedrukken vanaf 10 stuks." },
  { q: "Kan ik mijn eigen logo aanleveren?", a: "Ja. Je kunt je eigen logo of ontwerp aanleveren. We bekijken vervolgens of het bestand geschikt is voor de gewenste bedrukking." },
  { q: "Kan ik verschillende maten bestellen?", a: "Ja. Je kunt binnen een bestelling verschillende kledingmaten combineren, afhankelijk van het gekozen model en de beschikbaarheid." },
  { q: "Hoe snel kunnen jullie leveren?", a: "De levertijd hangt af van de oplage, de beschikbaarheid van de T-shirts en de gekozen druktechniek. Bij je offerte kunnen we aangeven wat haalbaar is." },
  { q: "Kan ik ook andere kleding laten bedrukken?", a: "Ja. Naast T-shirts verzorgen we onder andere bedrukte polo's, sweaters, hoodies en bedrijfskleding." },
];

const Hero = () => (
  <section className="relative min-h-[680px] overflow-hidden pt-28 md:min-h-[720px] md:pt-36">
    <img
      src={HERO_IMAGE}
      alt={H1}
      className="absolute inset-0 h-full w-full object-cover"
      width={1600}
      height={627}
      fetchPriority="high"
    />
    <div className="absolute inset-0 bg-surface-dark/70" />
    <div className="container relative z-10 flex min-h-[550px] items-center py-12 md:min-h-[584px] md:py-16">
      <div className="max-w-3xl text-surface-dark-foreground">
        <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">{H1}</h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-surface-dark-foreground/90 md:text-lg">
          Wil je T-shirts laten bedrukken voor je bedrijf, vereniging of evenement in Rotterdam? Bij Magic Print kun je terecht voor bedrukte T-shirts vanaf 10 stuks.
        </p>
        <ul className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-7">
          {["Vanaf 10 stuks", "Eigen productie", "Persoonlijk advies"].map((usp) => (
            <li key={usp} className="flex items-center gap-2 font-semibold">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                <Check className="h-3 w-3" />
              </span>
              {usp}
            </li>
          ))}
        </ul>
        <Button size="lg" className="mt-8" asChild>
          <Link to="/offerte">
            Vraag vrijblijvend een offerte aan <ArrowRight />
          </Link>
        </Button>
      </div>
    </div>
  </section>
);

// Full-bleed band inside PageLayout's centred container (same look as the branch page sections).
const bleed = "mx-[calc(50%-50vw)]";
const gridCls = "grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16";

const TShirtBedrukkenRotterdam = () => (
  <PageLayout
    title={H1}
    seoTitle="T-shirts bedrukken Rotterdam | Vanaf 10 stuks – Magic Print"
    metaDescription="T-shirts bedrukken in Rotterdam? Magic Print bedrukt T-shirts vanaf 10 stuks. Eigen productie, verschillende druktechnieken en persoonlijk advies."
    ogImage={HERO_IMAGE}
    hideDefaultCta
    hero={<Hero />}
  >
    <section className="-mt-16 py-14 md:-mt-20 md:py-16">
      <div className={gridCls}>
        <div>
          <h2 className={h2Cls}>T-shirts met je eigen logo of ontwerp</h2>
          <div className={bodyCls}>
            <p>Een bedrukt T-shirt wordt voor allerlei doeleinden gebruikt. Denk aan werkkleding voor medewerkers, shirts voor een sportevenement of kleding voor een vereniging.</p>
            <p>Magic Print is gevestigd in Den Haag. Vanuit onze eigen drukkerij verzorgen we bedrukte T-shirts voor bedrijven, verenigingen en organisaties in Rotterdam en omgeving.</p>
            <p>Bij Magic Print kun je kiezen uit verschillende modellen, kleuren en kwaliteiten. We kijken daarbij niet alleen naar het uiterlijk, maar ook naar het gebruik. Een T-shirt dat dagelijks gedragen en gewassen wordt, stelt andere eisen dan een shirt voor een eenmalig evenement.</p>
            <p>
              Van een eenvoudig logo op de borst tot een grote opdruk op de rug: heb je al een logo of ontwerp? Dan bekijken we welke bedrukking daar het beste bij past. Meer over de mogelijkheden lees je op onze pagina{" "}
              <Link to="/t-shirt-bedrukken" className={linkCls}>T-shirt bedrukken</Link>.
            </p>
          </div>
        </div>
        <img
          src={samplesClothing}
          alt="Bedrukte T-shirts in verschillende kleuren"
          className="aspect-[4/3] w-full rounded-2xl object-cover"
          width={800}
          height={600}
          loading="lazy"
          decoding="async"
        />
      </div>
    </section>

    <section className={`${bleed} bg-secondary py-16 md:py-24`}>
      <div className="container">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <h2 className={h2Cls}>Welke druktechniek past bij jouw T-shirts?</h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            Niet ieder ontwerp wordt op dezelfde manier bedrukt. De keuze hangt onder andere af van het aantal shirts, de kleuren in het ontwerp en het materiaal. Je hoeft vooraf niet te weten welke techniek je nodig hebt. Daar adviseren we je graag over.
          </p>
        </div>
        <TechniqueCards items={["zeefdruk", "transferdruk", "dtf"]} />
      </div>
    </section>

    <section className={`${bleed} bg-surface-dark py-16 text-surface-dark-foreground md:py-24`}>
      <div className="container max-w-4xl text-center">
        <h2 className="text-3xl font-bold md:text-4xl">T-shirts bedrukken vanaf 10 stuks</h2>
        <div className="mt-6 space-y-4 text-lg leading-relaxed text-surface-dark-foreground/80">
          <p>Bij Magic Print kun je T-shirts laten bedrukken vanaf 10 stuks, met verschillende druktechnieken.</p>
          <p>De prijs hangt af van verschillende factoren, zoals het gekozen T-shirt, de oplage, de druktechniek en het formaat van de bedrukking. Een klein borstlogo vraagt bijvoorbeeld om een andere aanpak dan een grote afbeelding op de voor- en achterkant.</p>
          <p>Daarom werken we met offertes op maat. Zo weet je vooraf wat je bestelling gaat kosten.</p>
        </div>
        <div className="mt-8 flex justify-center">
          <Button size="lg" asChild>
            <Link to="/offerte">Vraag vrijblijvend een offerte aan <ArrowRight /></Link>
          </Button>
        </div>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className={gridCls}>
        <img
          src={teamShirtsAsset.url}
          alt="T-shirts bedrukt voor bedrijven in Rotterdam"
          className="aspect-[4/3] w-full rounded-2xl object-cover object-top lg:order-1"
          width={600}
          height={400}
          loading="lazy"
          decoding="async"
        />
        <div className="lg:order-2">
          <h2 className={h2Cls}>T-shirts laten bedrukken voor bedrijven in Rotterdam</h2>
          <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
            <p>We verzorgen bedrukte T-shirts voor bedrijven, organisaties en verenigingen in Rotterdam en omgeving.</p>
            <p>Denk aan horecamedewerkers, bouwbedrijven, evenementenorganisaties en sportverenigingen.</p>
            <p>Naast T-shirts kun je bij ons ook terecht voor andere kleding, zoals polo's, sweaters en bedrijfskleding.</p>
            <p>Wil je meerdere soorten kleding laten bedrukken met hetzelfde logo? Dan kunnen we de mogelijkheden samen bekijken.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="border-t border-border py-16 md:py-24">
      <div className="mx-auto max-w-4xl text-center">
        <h2 className={h2Cls}>T-shirts laten bedrukken in Rotterdam? Vraag een offerte aan</h2>
        <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-muted-foreground">
          Heb je een ontwerp en weet je ongeveer hoeveel T-shirts je nodig hebt? Stuur ons je wensen en eventueel je logo. We bekijken de mogelijkheden en maken een passende offerte.
        </p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Button size="lg" asChild>
            <Link to="/offerte">Vraag vrijblijvend een offerte aan <ArrowRight /></Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/contact">Contact opnemen</Link>
          </Button>
        </div>
      </div>
    </section>

    {/* Extra bottom space so the floating quick-contact bar never covers the FAQ. */}
    <section className={`${bleed} -mb-20 bg-secondary pb-32 pt-16 md:pb-28 md:pt-24`} id="veelgestelde-vragen">
      <div className="container max-w-3xl">
        <h2 className={`${h2Cls} mb-10 text-center`}>Veelgestelde vragen</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`faq-${i}`}
              className="rounded-lg border border-border bg-card px-6 data-[state=open]:border-primary/40 [&_[role=region][data-state=closed]]:hidden"
            >
              <AccordionTrigger className="text-left font-medium hover:no-underline">
                <h3 className="text-base font-medium text-foreground">{f.q}</h3>
              </AccordionTrigger>
              <AccordionContent forceMount className="data-[state=closed]:hidden leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  </PageLayout>
);

export default TShirtBedrukkenRotterdam;
